var _a;
import { areCookiesAuthorized, combine, createEventRateLimiter, Observable, trackRuntimeError, canUseEventBridge, getEventBridge, getRelativeTime, startInternalMonitoring, initConsoleObservable, ConsoleApiName, ErrorSource, } from '@datadog/browser-core';
import { trackNetworkError } from '../domain/trackNetworkError';
import { StatusType } from '../domain/logger';
import { startLogsSessionManager, startLogsSessionManagerStub } from '../domain/logsSessionManager';
import { startLoggerBatch } from '../transport/startLoggerBatch';
var LogStatusForApi = (_a = {},
    _a[ConsoleApiName.log] = StatusType.info,
    _a[ConsoleApiName.debug] = StatusType.debug,
    _a[ConsoleApiName.info] = StatusType.info,
    _a[ConsoleApiName.warn] = StatusType.warn,
    _a[ConsoleApiName.error] = StatusType.error,
    _a);
export function startLogs(configuration, logger) {
    var internalMonitoring = startInternalMonitoring(configuration);
    var rawErrorObservable = new Observable();
    if (configuration.forwardErrorsToLogs) {
        trackRuntimeError(rawErrorObservable);
        trackNetworkError(configuration, rawErrorObservable);
    }
    var consoleObservable = initConsoleObservable(configuration.forwardConsoleLogs);
    var session = areCookiesAuthorized(configuration.cookieOptions) && !canUseEventBridge()
        ? startLogsSessionManager(configuration)
        : startLogsSessionManagerStub(configuration);
    return doStartLogs(configuration, rawErrorObservable, consoleObservable, internalMonitoring, session, logger);
}
export function doStartLogs(configuration, rawErrorObservable, consoleObservable, internalMonitoring, sessionManager, logger) {
    internalMonitoring.setExternalContextProvider(function () {
        var _a;
        return combine({ session_id: (_a = sessionManager.findTrackedSession()) === null || _a === void 0 ? void 0 : _a.id }, getRUMInternalContext(), {
            view: { name: null, url: null, referrer: null },
        });
    });
    var assemble = buildAssemble(sessionManager, configuration, reportRawError);
    var onLogEventCollected;
    if (canUseEventBridge()) {
        var bridge_1 = getEventBridge();
        onLogEventCollected = function (message) { return bridge_1.send('log', message); };
    }
    else {
        var batch_1 = startLoggerBatch(configuration);
        onLogEventCollected = function (message) { return batch_1.add(message); };
    }
    function reportRawError(error) {
        var messageContext = {
            date: error.startClocks.timeStamp,
            error: {
                kind: error.type,
                origin: error.source,
                stack: error.stack,
            },
        };
        if (error.resource) {
            messageContext.http = {
                method: error.resource.method,
                status_code: error.resource.statusCode,
                url: error.resource.url,
            };
        }
        logger.error(error.message, messageContext);
    }
    function reportConsoleLog(log) {
        var messageContext;
        if (log.api === ConsoleApiName.error) {
            messageContext = {
                error: {
                    origin: ErrorSource.CONSOLE,
                    stack: log.stack,
                },
            };
        }
        logger.log(log.message, messageContext, LogStatusForApi[log.api]);
    }
    rawErrorObservable.subscribe(reportRawError);
    consoleObservable.subscribe(reportConsoleLog);
    return function (message, currentContext) {
        var contextualizedMessage = assemble(message, currentContext);
        if (contextualizedMessage) {
            onLogEventCollected(contextualizedMessage);
        }
    };
}
export function buildAssemble(sessionManager, configuration, reportRawError) {
    var _a;
    var logRateLimiters = (_a = {},
        _a[StatusType.error] = createEventRateLimiter(StatusType.error, configuration.eventRateLimiterThreshold, reportRawError),
        _a[StatusType.warn] = createEventRateLimiter(StatusType.warn, configuration.eventRateLimiterThreshold, reportRawError),
        _a[StatusType.info] = createEventRateLimiter(StatusType.info, configuration.eventRateLimiterThreshold, reportRawError),
        _a[StatusType.debug] = createEventRateLimiter(StatusType.debug, configuration.eventRateLimiterThreshold, reportRawError),
        _a['custom'] = createEventRateLimiter('custom', configuration.eventRateLimiterThreshold, reportRawError),
        _a);
    return function (message, currentContext) {
        var _a, _b;
        var startTime = message.date ? getRelativeTime(message.date) : undefined;
        var session = sessionManager.findTrackedSession(startTime);
        if (!session) {
            return undefined;
        }
        var contextualizedMessage = combine({ service: configuration.service, session_id: session.id }, currentContext, getRUMInternalContext(startTime), message);
        if (((_a = configuration.beforeSend) === null || _a === void 0 ? void 0 : _a.call(configuration, contextualizedMessage)) === false ||
            ((_b = logRateLimiters[contextualizedMessage.status]) !== null && _b !== void 0 ? _b : logRateLimiters['custom']).isLimitReached()) {
            return undefined;
        }
        return contextualizedMessage;
    };
}
function getRUMInternalContext(startTime) {
    var rum = window.DD_RUM;
    return rum && rum.getInternalContext ? rum.getInternalContext(startTime) : undefined;
}
//# sourceMappingURL=startLogs.js.map